import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import {
  Plus, Upload, Download, Trash2, Check, Clock, AlertCircle, XCircle, Minus, ChevronDown
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || '';

const STATUS_OPTIONS = [
  { value: 'pending',        label: 'Pending',        color: '#6B7280' },
  { value: 'in_progress',   label: 'In Progress',    color: '#F59E0B' },
  { value: 'received',      label: 'Received',       color: '#3B82F6' },
  { value: 'complete',      label: 'Complete',       color: '#10B981' },
  { value: 'not_applicable', label: 'N/A',            color: '#9CA3AF' },
];

const PRIORITY_OPTIONS = [
  { value: 'high',   label: 'High',   color: '#EF4444' },
  { value: 'medium', label: 'Medium', color: '#F59E0B' },
  { value: 'low',    label: 'Low',    color: '#6B7280' },
];

const CATEGORIES = [
  'General', 'Financial', 'Legal', 'Operational', 'HR', 'IT',
  'Insurance', 'Facilities', 'Sales', 'Procurement', 'Tax', 'Environmental',
];

function statusIcon(status) {
  switch (status) {
    case 'complete':       return <Check size={13} />;
    case 'in_progress':    return <Clock size={13} />;
    case 'received':       return <AlertCircle size={13} />;
    case 'not_applicable': return <Minus size={13} />;
    default:               return <XCircle size={13} />;
  }
}

export default function IRLModule({ dealId, canEdit, orgUsers = [] }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showAddForm, setShowAddForm] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [importError, setImportError] = useState('');
  const [form, setForm] = useState({ description: '', category: 'General', priority: 'medium', assignee: '', dueDate: '', notes: '' });
  const [formSaving, setFormSaving] = useState(false);
  const importInputRef = useRef(null);

  useEffect(() => { if (dealId) fetchItems(); }, [dealId]); // eslint-disable-line

  const fetchItems = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/deals/${dealId}/irl`, { withCredentials: true });
      setItems(res.data || []);
    } catch (err) {
      setError('Failed to load IRL items.');
    } finally {
      setLoading(false);
    }
  };

  const updateItem = async (itemId, updates) => {
    setItems(prev => prev.map(i => i.id === itemId ? { ...i, ...updates } : i));
    try {
      await axios.put(`${API_URL}/api/deals/${dealId}/irl/${itemId}`, updates, { withCredentials: true });
    } catch (err) {
      console.error('Failed to update item', err);
      fetchItems();
    }
  };

  const deleteItem = async (itemId) => {
    setItems(prev => prev.filter(i => i.id !== itemId));
    try {
      await axios.delete(`${API_URL}/api/deals/${dealId}/irl/${itemId}`, { withCredentials: true });
    } catch (err) {
      console.error('Failed to delete item', err);
      fetchItems();
    }
  };

  const handleAddItem = async () => {
    if (!form.description.trim()) return;
    setFormSaving(true);
    try {
      const res = await axios.post(`${API_URL}/api/deals/${dealId}/irl`, form, { withCredentials: true });
      setItems(prev => [...prev, res.data]);
      setForm({ description: '', category: 'General', priority: 'medium', assignee: '', dueDate: '', notes: '' });
      setShowAddForm(false);
    } catch (err) {
      console.error('Failed to add item', err);
    } finally {
      setFormSaving(false);
    }
  };

  const handleImport = async (file) => {
    if (!file) return;
    setIsImporting(true);
    setImportError('');
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await axios.post(`${API_URL}/api/deals/${dealId}/irl/import`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setItems(prev => [...prev, ...(res.data.items || [])]);
    } catch (err) {
      setImportError(err.response?.data?.error || 'Import failed.');
    } finally {
      setIsImporting(false);
      if (importInputRef.current) importInputRef.current.value = '';
    }
  };

  const handleExport = () => {
    window.open(`${API_URL}/api/deals/${dealId}/irl/export`, '_blank');
  };

  const filtered = filterStatus === 'all' ? items : items.filter(i => i.status === filterStatus);
  const stats = {
    total: items.length,
    complete: items.filter(i => i.status === 'complete').length,
    pending: items.filter(i => i.status === 'pending').length,
    inProgress: items.filter(i => i.status === 'in_progress').length,
  };

  if (loading) return <div style={{ padding: 24, color: '#6B7280' }}>Loading IRL...</div>;

  return (
    <div className="deal-tab-panel">
      {/* Header */}
      <div className="deal-tab-header">
        <h3>Information Request List ({items.length})</h3>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {canEdit && (
            <>
              <input
                ref={importInputRef}
                type="file"
                style={{ display: 'none' }}
                accept=".csv,.xlsx,.xls"
                onChange={e => handleImport(e.target.files[0])}
              />
              <button className="deal-btn-add" onClick={() => importInputRef.current?.click()} disabled={isImporting}>
                <Upload size={15} />{isImporting ? 'Importing...' : 'Import CSV/Excel'}
              </button>
            </>
          )}
          <button className="deal-btn-add" onClick={handleExport} style={{ background: '#F3F4F6', color: '#374151' }}>
            <Download size={15} /> Export
          </button>
          {canEdit && (
            <button className="deal-btn-add" onClick={() => setShowAddForm(v => !v)}>
              <Plus size={15} /> Add Item
            </button>
          )}
        </div>
      </div>

      {/* Progress bar */}
      {items.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', gap: 16, marginBottom: 8, fontSize: 13, color: '#6B7280' }}>
            <span><strong style={{ color: '#10B981' }}>{stats.complete}</strong> complete</span>
            <span><strong style={{ color: '#F59E0B' }}>{stats.inProgress}</strong> in progress</span>
            <span><strong style={{ color: '#6B7280' }}>{stats.pending}</strong> pending</span>
            <span style={{ marginLeft: 'auto' }}>{stats.total} total</span>
          </div>
          <div style={{ height: 6, background: '#F3F4F6', borderRadius: 3, overflow: 'hidden' }}>
            <div style={{
              height: '100%',
              width: `${stats.total ? (stats.complete / stats.total) * 100 : 0}%`,
              background: '#10B981',
              borderRadius: 3,
              transition: 'width 0.3s ease',
            }} />
          </div>
        </div>
      )}

      {/* Import error */}
      {importError && (
        <div style={{ background: '#FEF2F2', color: '#EF4444', padding: '8px 12px', borderRadius: 6, fontSize: 13, marginBottom: 12 }}>
          {importError}
        </div>
      )}

      {/* Add item form */}
      {showAddForm && (
        <div style={{ background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 8, padding: 16, marginBottom: 16 }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
            <input
              style={{ flex: 2, minWidth: 200, padding: '7px 10px', border: '1px solid #D1D5DB', borderRadius: 6, fontSize: 13 }}
              placeholder="Description *"
              value={form.description}
              onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
            />
            <select
              style={{ padding: '7px 10px', border: '1px solid #D1D5DB', borderRadius: 6, fontSize: 13 }}
              value={form.category}
              onChange={e => setForm(p => ({ ...p, category: e.target.value }))}
            >
              {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <select
              style={{ padding: '7px 10px', border: '1px solid #D1D5DB', borderRadius: 6, fontSize: 13 }}
              value={form.priority}
              onChange={e => setForm(p => ({ ...p, priority: e.target.value }))}
            >
              {PRIORITY_OPTIONS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
            <select
              style={{ padding: '7px 10px', border: '1px solid #D1D5DB', borderRadius: 6, fontSize: 13 }}
              value={form.assignee}
              onChange={e => setForm(p => ({ ...p, assignee: e.target.value }))}
            >
              <option value="">Assignee</option>
              {orgUsers.map(u => {
                const name = u.full_name || u.name || u.email;
                return <option key={u.id} value={name}>{name}</option>;
              })}
            </select>
            <input
              type="date"
              style={{ padding: '7px 10px', border: '1px solid #D1D5DB', borderRadius: 6, fontSize: 13 }}
              value={form.dueDate}
              onChange={e => setForm(p => ({ ...p, dueDate: e.target.value }))}
            />
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button
              style={{ padding: '6px 14px', borderRadius: 6, border: '1px solid #D1D5DB', background: '#fff', fontSize: 13, cursor: 'pointer' }}
              onClick={() => setShowAddForm(false)}
            >Cancel</button>
            <button
              style={{ padding: '6px 14px', borderRadius: 6, background: '#1F2937', color: '#fff', border: 'none', fontSize: 13, cursor: 'pointer' }}
              onClick={handleAddItem}
              disabled={formSaving || !form.description.trim()}
            >{formSaving ? 'Adding...' : 'Add Item'}</button>
          </div>
        </div>
      )}

      {/* Status filter */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
        <button
          onClick={() => setFilterStatus('all')}
          style={{
            padding: '4px 12px', borderRadius: 20, border: '1px solid #E5E7EB', fontSize: 12,
            background: filterStatus === 'all' ? '#1F2937' : '#fff',
            color: filterStatus === 'all' ? '#fff' : '#374151',
            cursor: 'pointer',
          }}
        >All ({items.length})</button>
        {STATUS_OPTIONS.map(s => (
          <button
            key={s.value}
            onClick={() => setFilterStatus(s.value)}
            style={{
              padding: '4px 12px', borderRadius: 20, border: `1px solid ${s.color}40`, fontSize: 12,
              background: filterStatus === s.value ? s.color : '#fff',
              color: filterStatus === s.value ? '#fff' : s.color,
              cursor: 'pointer',
            }}
          >{s.label} ({items.filter(i => i.status === s.value).length})</button>
        ))}
      </div>

      {/* Items table */}
      {error && <div style={{ color: '#EF4444', fontSize: 13 }}>{error}</div>}
      {filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px 0', color: '#9CA3AF' }}>
          {items.length === 0
            ? 'No items yet. Import a CSV/Excel file or add items manually.'
            : 'No items match the selected filter.'}
        </div>
      ) : (
        <div style={{ border: '1px solid #E5E7EB', borderRadius: 8, overflow: 'hidden' }}>
          {/* Table header */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '32px 1fr 100px 80px 80px 110px 110px 40px',
            background: '#F9FAFB',
            borderBottom: '1px solid #E5E7EB',
            padding: '8px 12px',
            fontSize: 11,
            fontWeight: 600,
            color: '#6B7280',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
          }}>
            <span>#</span>
            <span>Description</span>
            <span>Category</span>
            <span>Priority</span>
            <span>Status</span>
            <span>Assignee</span>
            <span>Due Date</span>
            <span></span>
          </div>
          {/* Rows */}
          {filtered.map((item, idx) => {
            const status = STATUS_OPTIONS.find(s => s.value === item.status) || STATUS_OPTIONS[0];
            const priority = PRIORITY_OPTIONS.find(p => p.value === item.priority) || PRIORITY_OPTIONS[1];
            return (
              <div
                key={item.id}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '32px 1fr 100px 80px 80px 110px 110px 40px',
                  padding: '10px 12px',
                  borderBottom: '1px solid #F3F4F6',
                  alignItems: 'center',
                  background: item.status === 'complete' ? '#F9FAFB' : '#fff',
                  opacity: item.status === 'complete' ? 0.75 : 1,
                }}
              >
                <span style={{ fontSize: 11, color: '#9CA3AF' }}>{item.request_number || idx + 1}</span>
                <span style={{
                  fontSize: 13,
                  color: '#111827',
                  textDecoration: item.status === 'complete' ? 'line-through' : 'none',
                  wordBreak: 'break-word',
                }}>{item.description}</span>
                <span style={{ fontSize: 12, color: '#6B7280' }}>{item.category}</span>
                <span style={{
                  fontSize: 11,
                  color: priority.color,
                  fontWeight: 500,
                }}>{priority.label}</span>
                {canEdit ? (
                  <select
                    value={item.status}
                    onChange={e => updateItem(item.id, { status: e.target.value })}
                    style={{
                      fontSize: 11,
                      color: status.color,
                      border: 'none',
                      background: 'transparent',
                      cursor: 'pointer',
                      fontWeight: 600,
                      padding: 0,
                    }}
                  >
                    {STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                ) : (
                  <span style={{ fontSize: 11, color: status.color, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
                    {statusIcon(item.status)} {status.label}
                  </span>
                )}
                {canEdit ? (
                  <select
                    style={{ fontSize: 12, border: 'none', background: 'transparent', color: '#374151', width: '100%', cursor: 'pointer', padding: 0 }}
                    value={item.assignee || ''}
                    onChange={e => updateItem(item.id, { assignee: e.target.value })}
                  >
                    <option value="">—</option>
                    {orgUsers.map(u => {
                      const name = u.full_name || u.name || u.email;
                      return <option key={u.id} value={name}>{name}</option>;
                    })}
                  </select>
                ) : (
                  <span style={{ fontSize: 12, color: '#374151' }}>{item.assignee || '—'}</span>
                )}
                {canEdit ? (
                  <input
                    type="date"
                    style={{ fontSize: 12, border: 'none', background: 'transparent', color: '#6B7280', width: '100%', cursor: 'text', padding: 0 }}
                    defaultValue={item.due_date ? item.due_date.split('T')[0] : ''}
                    onBlur={e => { if (e.target.value !== (item.due_date || '').split('T')[0]) updateItem(item.id, { due_date: e.target.value }); }}
                  />
                ) : (
                  <span style={{ fontSize: 12, color: '#6B7280' }}>
                    {item.due_date ? new Date(item.due_date).toLocaleDateString() : '—'}
                  </span>
                )}
                {canEdit && (
                  <button
                    onClick={() => deleteItem(item.id)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#D1D5DB', padding: 4 }}
                    title="Delete"
                  >
                    <Trash2 size={13} />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
